import type { TvChannelWithEpg } from '@setplex/tria-api'
import type { Store } from 'effector'
import type { NpawPlayerOptions } from '~/features/npaw'
import type { TvChannelWithLiveEpg } from '../index.h'

import { A, D, flow } from '@mobily/ts-belt'
import { combine, sample } from 'effector'
import { model as i18n } from '~/core/i18n'
import { epgProgramsModel } from '~/entities/epg/programs'
import { selectEpgChannelModel } from '~/features/epg/channels/select'
import { selectEpgDateModel } from '~/features/epg/dates/select'
import { loadEpgProgramsModel } from '~/features/epg/programs/load'
import { createNpawVideoOptions, featuresNpawModel } from '~/features/npaw'
import { ssePurchasesModel } from '~/features/payment/sse-purchases'
import { model as clock } from '~/shared/clock'
import { epgChannelsWidgetModel } from '~/widgets/epg/channels'
import { epgInfoWidgetModel } from '~/widgets/epg/info'
import { epgTimelineWidgetModel } from '~/widgets/epg/timeline'
import {
  clipToRange,
  extendWithProgram,
  formatProgramToLocalizedView,
  formatProgramToView,
  formatToLocalizedView,
} from '../lib/epg'

// currently active category channels merged with their epg data
export const $channels: Store<TvChannelWithEpg[]> = combine(
  {
    channels: epgChannelsWidgetModel.$activeList,
    epg: epgProgramsModel.$epg,
  },
  ({ channels, epg: [programs] }) =>
    A.map(channels, extendWithProgram(programs))
)

// live epg data for currently visible range, updates each minute
export const $live: Store<TvChannelWithLiveEpg[]> = combine(
  {
    channels: $channels,
    start: epgTimelineWidgetModel.$start,
    duration: epgTimelineWidgetModel.$duration,
    now: clock.$minutes,
    adjust: ssePurchasesModel.$channel,
    i18n: i18n.$t,
  },
  ({ channels, start, duration, now, adjust, i18n: { t } }) =>
    channels.map((channel) =>
      flow(
        clipToRange([start, start + duration]),
        formatToLocalizedView({ adjust, t }),
        D.updateUnsafe(
          'program',
          A.map(
            flow(
              formatProgramToView({ now }),
              formatProgramToLocalizedView({
                t,
                blockedByAcl: Boolean(channel.blockedByAcl),
              })
            )
          )
        )
      )(channel)
    ) as TvChannelWithLiveEpg[]
)

export const $playerNpawData: Store<NpawPlayerOptions | null> = combine(
  {
    playerNpaw: featuresNpawModel.$playerNpaw,
    selectedEpgChannel: selectEpgChannelModel.$active,
    activeProgram: epgInfoWidgetModel.$playing,
  },
  ({ playerNpaw, selectedEpgChannel, activeProgram }) => {
    if (!playerNpaw || !selectedEpgChannel || !activeProgram) return null

    const videoOptions = createNpawVideoOptions({
      ...selectedEpgChannel,
      program: activeProgram.programTitle,
      type: 'tv',
    })

    return {
      ...playerNpaw,
      videoOptions,
    } satisfies NpawPlayerOptions
  }
)

// on every active channels update, and on every dates update - ask to load programs
sample({
  clock: [epgChannelsWidgetModel.$activeList, selectEpgDateModel.$active],
  source: {
    channels: epgChannelsWidgetModel.$activeList,
    date: selectEpgDateModel.$active,
  },
  filter: ({ channels, date }) => date != null && channels.length > 0,

  fn: ({ channels, date }) => ({ channels, date: date! }),
  target: loadEpgProgramsModel.load,
})
